import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import useStore from "./store/useStore";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import ProductsPage from "./pages/Products/ProductsPage";
import ProductDetailPage from "./pages/Products/ProductDetailPage";
import CartPage from "./pages/Cart/CartPage";
import CheckoutPage from "./pages/Checkout/CheckoutPage";
import OffersPage from "./pages/OffersPage";

import CashfreeReturn from "./pages/CashfreeReturn";
import SubscriptionMandateReturn from "./pages/SubscriptionMandateReturn";
import SubscriptionStartPage from "./pages/SubscriptionStartPage";
import ContactUs from "./pages/ContactUs";
import AboutUs from "./pages/About";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AccountPage from "./pages/Account/AccountPage";
import ProfileSection from "./pages/Account/sections/ProfileSection";
import OrdersSection from "./pages/Account/sections/OrdersSection";
import ReturnsSection from "./pages/Account/sections/ReturnsSection";
import RequestReturnPage from "./pages/Returns/RequestReturnPage";
import TrackOrderSection from "./pages/Account/sections/TrackOrderSection";
import OrderDetailSection from "./pages/Account/sections/OrderDetailSection";
import WishlistSection from "./pages/Account/sections/WishlistSection";
import AddressesSection from "./pages/Account/sections/AddressesSection";
import SubscriptionsSection from "./pages/Account/sections/SubscriptionsSection";
import SubscriptionRoutePage from "./pages/Account/sections/subscriptions/SubscriptionRoutePage";
import ReferralSection from "./pages/Account/sections/ReferralSection";
import NotificationsSection from "./pages/Account/sections/NotificationsSection";
import NotificationDetailPage from "./pages/Account/sections/NotificationDetailPage";
import HelpSection from "./pages/Account/sections/HelpSection";
import WalletSection from "./pages/Account/sections/WalletSection";
import AuthModal from "./components/AuthModal";
import LocationModal from "./components/LocationModal";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "react-hot-toast";
import { getCookie } from "./utils/cookies";
import "./index.css";

function App() {
  const checkAuth = useStore((s) => s.checkAuth);
  const loadGuestCart = useStore((s) => s.loadGuestCart);
  const fetchUserAddresses = useStore((s) => s.fetchUserAddresses);

  useEffect(() => {
    // checkAuth validates the authToken cookie.
    // After it resolves, loadGuestCart() is safe to call — it is a no-op when logged in.
    checkAuth().finally(() => {
      loadGuestCart();
      const token = getCookie("authToken");
      if (token && typeof fetchUserAddresses === "function") {
        fetchUserAddresses();
      }
    });
  }, [checkAuth, loadGuestCart, fetchUserAddresses]);

  return (
    <Router>
      <div className="clip-x-only min-h-screen bg-gray-50 flex flex-col">
        <ScrollToTop />
        <Header />
        <main className="clip-x-only flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:categoryId" element={<ProductsPage />} />
            <Route path="/product/:productSlug" element={<ProductDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/offers" element={<OffersPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/payment/return" element={<CashfreeReturn />} />
            <Route path="/subscription/mandate/return" element={<SubscriptionMandateReturn />} />
            <Route path="/subscription/start" element={<SubscriptionStartPage />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<Navigate to="/policy/terms" replace />} />
            <Route path="/refund-policy" element={<Navigate to="/policy/refund" replace />} />
            <Route path="/policy/:policyType" element={<PrivacyPolicy />} />
            <Route path="/returns" element={<Navigate to="/account/returns" replace />} />
            <Route path="/returns/request" element={<RequestReturnPage />} />
            <Route path="/returns/request/:orderId" element={<RequestReturnPage />} />
            <Route path="/order-return" element={<RequestReturnPage />} />

            <Route path="/account" element={<AccountPage />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<ProfileSection />} />
              <Route path="orders" element={<OrdersSection />} />
              <Route path="orders/:orderId" element={<OrderDetailSection />} />
              <Route path="order/:orderId" element={<OrderDetailSection />} />
              <Route path="order-detail/:orderId" element={<OrderDetailSection />} />
              <Route path="returns" element={<ReturnsSection />} />
              <Route path="track" element={<TrackOrderSection />} />
              <Route path="track/:orderId" element={<TrackOrderSection />} />
              <Route path="wishlist" element={<WishlistSection />} />
              <Route path="subscriptions" element={<SubscriptionsSection />} />
              <Route path="subscriptions/:subscriptionId/:action" element={<SubscriptionRoutePage />} />
              <Route path="referral" element={<ReferralSection />} />
              <Route path="addresses" element={<AddressesSection />} />
              <Route path="notifications" element={<NotificationsSection />} />
              <Route path="notifications/:notificationId" element={<NotificationDetailPage />} />
              <Route path="help" element={<HelpSection />} />
              <Route path="wallet" element={<WalletSection />} />
            </Route>
          </Routes>
        </main>
        <Footer />
        <AuthModal />
        <LocationModal />
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: "12px",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
            },
            success: {
              duration: 3000,
              style: {
                background: "#f0fdf4",
                color: "#15803d",
                border: "1px solid #bbf7d0",
              },
            },
            error: {
              duration: 4000,
              style: {
                background: "#fef2f2",
                color: "#dc2626",
                border: "1px solid #fecaca",
              },
            },
            custom: { duration: 6000 },
          }}
        />
      </div>
    </Router>
  );
}

export default App;
